/**
 * Domain contracts. Every package imports its types from here and none of them
 * redeclare a shape that lives in this file.
 *
 * Zod schemas are the single source of truth; TypeScript types are inferred
 * from them so validation and typing can never drift apart.
 */
import { z } from 'zod';
export const uuid = z.string().uuid();
export const isoDateTime = z.string().datetime({ offset: true });
export const cents = z.number().int();
export const nonNegativeCents = z.number().int().min(0);
export const positiveCents = z.number().int().positive();
// ---------------------------------------------------------------------------
// Enumerations. These mirror the Postgres enums exactly.
// ---------------------------------------------------------------------------
export const StaffRole = z.enum(['admin', 'staff']);
export const ShiftStatus = z.enum(['open', 'closed']);
export const SaleStatus = z.enum(['completed', 'voided']);
export const PaymentMethod = z.enum(['cash', 'card']);
export const CardType = z.enum(['credit', 'debit']);
export const DiscountKind = z.enum(['percent', 'amount']);
// ---------------------------------------------------------------------------
// Catalog. These shapes travel from server to terminal and are read-only there.
// ---------------------------------------------------------------------------
export const Category = z.object({
    id: uuid,
    name: z.string().min(1),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    // stock & media: categories gained an optional image, same upload path as
    // products (db/migrations/005_stock_media.sql). `.default(null)` keeps this
    // additive — every existing literal Category object across the repo that
    // predates this field still parses.
    imageUrl: z.string().nullable().default(null),
    sortOrder: z.number().int(),
    active: z.boolean(),
    updatedAt: isoDateTime,
    deletedAt: isoDateTime.nullable(),
});
/**
 * One choice of a product that offers a choice (a flavour). It carries its
 * own price, cost and stock; choosing several is charged as the most
 * expensive chosen option (see `optionsUnitPriceCents`). Cost is admin-only
 * and, like `Product.costCents`, never present in the synced catalog.
 */
export const ProductOption = z.object({
    id: uuid,
    name: z.string().min(1),
    priceCents: nonNegativeCents,
    costCents: nonNegativeCents.nullable().optional(),
    /** Discount floor derived from cost server-side; synced (the cost figure is not). */
    minSalePriceCents: nonNegativeCents.nullable().optional(),
    sortOrder: z.number().int(),
    active: z.boolean(),
    /** Server-authoritative; an oversell is legal and shows as a negative number. */
    stockQty: z.number().finite().default(0),
    /**
     * An EXTRA (the cheeses): its price is added to the flavour's, it is optional
     * and never counts as a flavour. Absent = a flavour.
     */
    isAddon: z.boolean().optional(),
    /** The column the option is shown under in the picker; absent = the product's own list. */
    groupName: z.string().nullable().optional(),
});
/** What a sale line remembers of a chosen option (a snapshot, like the name and price). */
export const SelectedOption = z.object({
    id: uuid,
    name: z.string().min(1),
    priceCents: nonNegativeCents,
    minSalePriceCents: nonNegativeCents.nullable().optional(),
    /** True for an extra (a cheese): priced on top of the flavour, never a flavour itself. */
    isAddon: z.boolean().optional(),
});
export const Product = z.object({
    id: uuid,
    sku: z.string().min(1),
    name: z.string().min(1),
    categoryId: uuid.nullable(),
    priceCents: nonNegativeCents,
    /**
     * Admin-only unit cost in centavos, optional (null/absent = unknown).
     * DELIBERATELY optional rather than nullable-required: the catalog a
     * terminal pulls never carries it — cost and the profit derived from it
     * are admin figures, kept off staff devices like shift variance — so a
     * synced Product parses without it, and the admin API includes it.
     */
    costCents: nonNegativeCents.nullable().optional(),
    /**
     * Floor for discounting, SYNCED to terminals (2026-08-02): a discount
     * that would drop the ticket below the sum of these requires admin
     * authorization. Derived server-side from cost (today: equal to a
     * positive cost); deliberately its own field so the terminal enforces a
     * boundary without the catalog ever labeling anything "cost", and so
     * the derivation can change server-side without an app update.
     */
    minSalePriceCents: nonNegativeCents.nullable().optional(),
    imageUrl: z.string().nullable(),
    sortOrder: z.number().int(),
    active: z.boolean(),
    // stock & media: server-authoritative running count, synced to every
    // terminal (db/migrations/005_stock_media.sql). Deliberately not
    // `nonNegativeCents`-style clamped — an oversell is legal and shows as a
    // negative number, never a rejected sale. `.default(0)` keeps this
    // additive: any pre-existing literal Product object across the repo that
    // omits it still parses to zero.
    stockQty: z.number().finite().default(0),
    /**
     * Options (flavours). All three are optional so the type stays additive: a
     * product without them is exactly what it was before. `optionsLabel` is the
     * question asked («Sabor»); `optionsMax` how many may be chosen (null/absent
     * = no limit); `options` the choices. A product with at least one active
     * option is sold by choosing among them; its own `priceCents` is then only
     * the lowest option price (what a terminal that predates options would charge).
     */
    optionsLabel: z.string().nullable().optional(),
    optionsMax: z.number().int().min(1).nullable().optional(),
    /** How many extras may be chosen (null/absent = no limit). */
    addonsMax: z.number().int().min(1).nullable().optional(),
    /**
     * Mix price: from `mixMinFlavours` chosen flavours on, the unit price is at
     * least `mixPriceCents` (never below the dearest flavour). Both or neither;
     * absent = a mix is charged as its dearest flavour. See `optionsUnitPriceCents`.
     */
    mixMinFlavours: z.number().int().min(2).nullable().optional(),
    mixPriceCents: nonNegativeCents.nullable().optional(),
    /**
     * Whether a sale of this product appears on the kitchen order (the comanda).
     * Absent = true; jars and popcorn are false. Synced only when false.
     */
    needsKitchen: z.boolean().optional(),
    options: z.array(ProductOption).optional(),
    updatedAt: isoDateTime,
    deletedAt: isoDateTime.nullable(),
});
export const DiscountReason = z.object({
    id: uuid,
    label: z.string().min(1),
    requiresNote: z.boolean(),
    /**
     * Standard percentage this reason auto-fills when picked in the POS
     * (2026-08-02). null/absent = no standard, the operator chooses. Optional
     * so pre-existing literals and older servers' catalogs still parse.
     */
    defaultPct: z.number().min(0).max(100).nullable().optional(),
    /** Ticket subtotal (centavos) required before this reason is offered.
     * null/absent = always available. Born from «Mayoreo: only above $500». */
    minSubtotalCents: nonNegativeCents.nullable().optional(),
    sortOrder: z.number().int(),
    active: z.boolean(),
    updatedAt: isoDateTime,
    deletedAt: isoDateTime.nullable(),
});
export const PaymentTerminalProcessor = z.enum(['mercadopago', 'clip']);
/**
 * A registered Mercado Pago/Clip card-collection device. Deliberately not
 * named `Terminal` — that already means the POS device (the Sunmi tablet).
 * Non-secret (a device identifier, not a credential), synced to every POS
 * terminal as a new catalog array alongside discount reasons.
 */
export const PaymentTerminal = z.object({
    id: uuid,
    processor: PaymentTerminalProcessor,
    externalTerminalId: z.string().min(1),
    displayName: z.string().min(1),
    active: z.boolean(),
    updatedAt: isoDateTime,
    deletedAt: isoDateTime.nullable(),
});
/**
 * Staff record as it reaches a terminal. Carries PIN verification material for
 * offline sign-in and deliberately carries no password hash, no email, and
 * nothing else the terminal has no business holding.
 */
export const StaffPublic = z.object({
    id: uuid,
    code: z.string().min(1),
    displayName: z.string().min(1),
    role: StaffRole,
    discountCeilingPct: z.number().min(0).max(100),
    pinHash: z.string().min(1),
    pinSalt: z.string().min(1),
    pinIterations: z.number().int().positive(),
    active: z.boolean(),
    updatedAt: isoDateTime,
    deletedAt: isoDateTime.nullable(),
});
export const AppConfig = z.object({
    businessName: z.string().default('La cocina de Ana'),
    ticketHeader: z.array(z.string()).default([]),
    ticketFooter: z.array(z.string()).default([]),
    /** URL printed as a QR code at the very bottom of every ticket. Empty =
     * no QR (2026-08-02, client request: link to lacocinadeana.mx). */
    ticketQrUrl: z.string().default(''),
    printerWidthDots: z.number().int().default(384),
    printerColumns: z.number().int().default(32),
    syncIntervalSeconds: z.number().int().positive().default(60),
    pinMaxAttempts: z.number().int().positive().default(5),
    locale: z.string().default('es-MX'),
});
// ---------------------------------------------------------------------------
// Transactional shapes. These originate on the terminal, offline.
// ---------------------------------------------------------------------------
export const LineDiscount = z.object({
    kind: DiscountKind,
    value: z.number().nonnegative(),
    amountCents: nonNegativeCents,
    reasonId: uuid,
    note: z.string().nullable(),
    /** Set when the discount exceeded the staff ceiling and an admin authorized it. */
    authorizedByStaffId: uuid.nullable(),
});
export const SaleLine = z.object({
    id: uuid,
    lineNo: z.number().int().positive(),
    productId: uuid.nullable(),
    skuSnapshot: z.string(),
    nameSnapshot: z.string().min(1),
    unitPriceCents: nonNegativeCents,
    qty: z.number().positive(),
    discount: LineDiscount.nullable(),
    totalCents: nonNegativeCents,
    /** Chosen options of a product that offers them; absent/empty for any other line. */
    options: z.array(SelectedOption).optional(),
});
export const CashPayment = z.object({
    id: uuid,
    seq: z.number().int().positive(),
    method: z.literal('cash'),
    amountCents: positiveCents,
    tenderedCents: nonNegativeCents,
    changeCents: nonNegativeCents,
});
/**
 * The one source of truth for what a card brand/last-4 is allowed to look
 * like, shared by every zod schema below AND by the server's own direct-SQL
 * write path (`routes/payment-terminals.ts`), which reads these straight off
 * a provider's HTTP response and never goes through a zod schema at all
 * before writing to `terminal_charges` — a gap found in integrator review:
 * the regexes below only ever protected the POS's own outbound sale
 * submission, not the server's independent polling path. Exported so that
 * path can sanitize with the exact same rule instead of duplicating it.
 */
export const CARD_BRAND_PATTERN = /^[a-zA-Z]{2,20}$/;
export const CARD_LAST4_PATTERN = /^\d{4}$/;
/** Returns `value` unchanged if it matches `CARD_BRAND_PATTERN`, else null —
 * never throws. Mirrors the zod schemas' own `.catch(null)` behavior for
 * code paths that read a provider response directly, without going through
 * zod at all. */
export function sanitizeCardBrand(value) {
    return value && CARD_BRAND_PATTERN.test(value) ? value : null;
}
/** Same as `sanitizeCardBrand`, for the last-4-digits field. */
export function sanitizeCardLast4(value) {
    return value && CARD_LAST4_PATTERN.test(value) ? value : null;
}
export const CardPayment = z.object({
    id: uuid,
    seq: z.number().int().positive(),
    method: z.literal('card'),
    amountCents: positiveCents,
    cardType: CardType,
    authNumber: z.string().trim().min(1),
    processor: z.string().default('external_terminal'),
    processorRef: z.string().nullable().default(null),
    /** From the provider's own approved-charge response (e.g. "master",
     * "visa") — null for manual entry, since there is no provider response
     * to read it from. Shape-constrained as data hygiene (`.catch(null)`,
     * never rejects the sale over this — push stays client-authoritative);
     * `ticket.ts`'s own rendering separately allowlists which recognized
     * brands are actually printed, since "syntactically valid" and "safe to
     * show a customer" are different claims (found in integrator review). */
    cardBrand: z.string().regex(CARD_BRAND_PATTERN).nullable().catch(null),
    /** The card's own last 4 digits, from the provider's response — the
     * SAME four digits any physical receipt already prints, never the full
     * number. Null for manual entry. Exactly 4 digits when present,
     * `.catch(null)` otherwise — never rejects the sale over this. */
    cardLast4: z.string().regex(CARD_LAST4_PATTERN).nullable().catch(null),
});
export const Payment = z.discriminatedUnion('method', [CashPayment, CardPayment]);
// ---------------------------------------------------------------------------
// Payment terminal push (POST/GET /api/v1/payment-terminals/...). Shared
// between the POS, which sends the request and polls the status, and the
// server, which validates and answers both.
// ---------------------------------------------------------------------------
export const CreateTerminalChargeRequest = z.object({
    paymentTerminalId: uuid,
    amountCents: positiveCents,
    /** Minted client-side, same UUIDv7 convention as every other id this
     * terminal creates offline. Lets a retried POST (flaky network) land once. */
    idempotencyKey: uuid,
});
export const TerminalChargeStatus = z.enum(['pending', 'approved', 'declined', 'cancelled', 'expired']);
export const TerminalChargeResponse = z.object({
    id: uuid,
    status: TerminalChargeStatus,
    /** Present once status is 'approved'. Masked to last-4 outside EQUINOX_DEBUG
     * by logging/redact.ts's isAuthNumberKey — this field name is what it matches. */
    authNumber: z.string().nullable(),
    /**
     * Read from the provider's own approved-charge response. What is
     * actually confirmed, stated precisely (found in integrator review —
     * an earlier version of this comment overclaimed "credit_card/debit_card
     * on every real charge seen", but every real charge seen was credit):
     *   - OBSERVED live for Mercado Pago, 2026-10-16: two real approved
     *     charges both carried `transactions.payments[0].payment_method.type
     *     === 'credit_card'`.
     *   - DOCUMENTED, not yet observed live, for Mercado Pago: `debit_card`
     *     is the sibling value in MP's own general payment-method
     *     vocabulary (shared across all MP payment types, not Point-
     *     specific) — inferred to apply here too, not independently proven
     *     against a real debit charge.
     *   - OBSERVED live for Clip, 2026-10-16 (same day PinPad was enabled
     *     for our device): a real $140.00 MXN charge carried `results[].
     *     payment_method.type === 'credit_card'`, mapped correctly to
     *     `'credit'` — the first real confirmation of this field for Clip.
     *     `debit_card` remains unobserved, same caveat as Mercado Pago's.
     * Null whenever the provider's response didn't carry a recognized
     * value — the operator-confirmation tap (`CardPaymentPanel`'s "¿Crédito
     * o débito?") is the fallback for exactly that case, never removed.
     */
    // `.default(null)`, not a bare `.nullable()`: lets a fixture or a response
    // shaped before this field existed still parse, falling back to the
    // operator's own confirmation tap exactly as before rather than failing
    // to parse at all.
    cardType: CardType.nullable().default(null),
    /** From the provider's own approved-charge response — printed on the
     * terminal's own voucher today; added here so our ticket can carry the
     * same detail once the terminal's own receipt is suppressed
     * (config.point.print_on_terminal: 'no_ticket' for Mercado Pago). Shape
     * is constrained (short, letters only) purely as data hygiene — garbage
     * must never reach the database or the immutable audit_log — but a
     * syntactically valid value is NOT the same claim as "safe to show a
     * customer": `ticket.ts`'s own rendering only prints a brand it
     * recognizes against an explicit allowlist (found in integrator review:
     * a raw provider-internal code like "DEBVISA" is valid by this regex
     * but not something to print on a receipt), falling back to printing
     * just the last 4 digits with no brand for anything it doesn't.
     * `.catch(null)`, not a hard failure, so a response carrying a shape
     * this adapter doesn't recognize still parses as a whole. */
    cardBrand: z.string().regex(CARD_BRAND_PATTERN).nullable().catch(null),
    /** Exactly 4 digits when present. `.catch(null)`, not a hard failure —
     * a malformed value becomes null rather than failing the whole
     * response to parse, and null is already the documented "nothing to
     * print" case everywhere this field is read. */
    cardLast4: z.string().regex(CARD_LAST4_PATTERN).nullable().catch(null),
});
export const Sale = z
    .object({
    id: uuid,
    folio: z.string().min(1),
    shiftId: uuid,
    staffId: uuid,
    terminalId: uuid,
    soldAt: isoDateTime,
    lines: z.array(SaleLine).min(1),
    payments: z.array(Payment).min(1),
    subtotalCents: nonNegativeCents,
    discountCents: nonNegativeCents,
    totalCents: nonNegativeCents,
    status: SaleStatus.default('completed'),
})
    .superRefine((sale, ctx) => {
    if (sale.totalCents !== sale.subtotalCents - sale.discountCents) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'total must equal subtotal minus discount',
            path: ['totalCents'],
        });
    }
    const paid = sale.payments.reduce((a, p) => a + p.amountCents, 0);
    if (paid !== sale.totalCents) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `payments total ${paid} does not settle sale total ${sale.totalCents}`,
            path: ['payments'],
        });
    }
    const lineSum = sale.lines.reduce((a, l) => a + l.totalCents, 0);
    if (lineSum !== sale.totalCents) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `line totals ${lineSum} do not sum to sale total ${sale.totalCents}`,
            path: ['lines'],
        });
    }
});
export const Shift = z.object({
    id: uuid,
    staffId: uuid,
    terminalId: uuid,
    openedAt: isoDateTime,
    openingBalanceCents: nonNegativeCents,
    closedAt: isoDateTime.nullable(),
    closingBalanceCents: nonNegativeCents.nullable(),
    status: ShiftStatus,
});
/**
 * Server-side only. This shape must never be serialized to a terminal, because
 * it carries the figures a staff member is not allowed to see.
 */
export const ShiftReconciliation = z.object({
    shiftId: uuid,
    expectedCashCents: cents,
    declaredCashCents: cents,
    varianceCents: cents,
});
// ---------------------------------------------------------------------------
// stock & media — append-only stock ledger (db/migrations/005_stock_media.sql)
// ---------------------------------------------------------------------------
/** Mirrors the Postgres `stock_movement_reason` enum exactly. */
export const StockMovementReason = z.enum(['sale', 'adjustment', 'import']);
/**
 * One row of the append-only ledger behind `products.stock_qty`. Admin-only
 * (never travels to a terminal): a terminal only ever sees the resulting
 * `stockQty` on the synced `Product`, never the ledger that explains it.
 */
export const StockMovement = z.object({
    id: uuid,
    productId: uuid,
    delta: z.number().finite(),
    reason: StockMovementReason,
    saleId: uuid.nullable(),
    occurredAt: isoDateTime,
});
//# sourceMappingURL=domain.js.map